import { FilterContextData } from 'contexts/FilterContext';
import { cloneDeep, isEmpty, kebabCase, orderBy } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
    deleteTypeNameFromResponse,
    generateDefaultApplyFilter,
    getIntersectionOnApplyFilter,
} from 'utils/helperMethods';
import { useGrowthMarginDriversContext } from '../context/GrowthMarginDriversContext';
import { applyFilterMutation } from 'gqlQueries/filterQueries';
import { useMutation } from '@apollo/client';
import fetchProfitPoolData, { DimIdsFilter, PROFIT_POOL_API_LIMIT } from 'components/pages/ProfitPoolOverview/apiUtils';
import {
    addChildrenToNode,
    createChildNodesFromApiData,
    findPath,
    HierarchicalTreeNode,
} from '../data/hierarchicalTreeData';

const getOnlyVarsforApplyFilter = (selectedItem: any) => {
    return {
        attributeExId: selectedItem.attributeExId,
        attributeDesc: selectedItem.attributeDesc,
        dimension: selectedItem.dimension,
        attributeType: selectedItem.attributeType,
        isAttribute: selectedItem.isAttribute,
    };
};

const useDecompositionGraphData = () => {
    const [isDecompositionGraphDataLoading, setIsDecompositionGraphDataLoading] = useState(false);
    const { idForRestOfComponent, singleSelectedTier, timePeriodSelection } = useContext(FilterContextData);
    const { selectedNodes, selectedGrowthMarginDriver, decompositionGraphData, setDecompositionGraphData } =
        useGrowthMarginDriversContext();

    const [mutateApplyFilter] = useMutation(applyFilterMutation);

    const fetchNodeData = useCallback(
        async (node: HierarchicalTreeNode, dimIdsFilter: DimIdsFilter) => {
            try {
                const data = await fetchProfitPoolData({
                    selectedSubFilter: {
                        dataKey: node?.nextAttributeType || '',
                        dimension: node?.nextDimension || '',
                        isAttribute: node?.nextIsAttribute ?? true,
                    },
                    limit: PROFIT_POOL_API_LIMIT,
                    offset: 0,
                    kpi: selectedGrowthMarginDriver?.key || 'profit_absolute',
                    tierList: singleSelectedTier,
                    yearAgo: false,
                    groupedByList: ['attribute'],
                    dimIdsFilter: dimIdsFilter,
                    frequency: 'M',
                    timePeriod: timePeriodSelection ? JSON.parse(timePeriodSelection) : undefined,
                });

                return {
                    node,
                    nextAttributeType: node?.nextAttributeType || '',
                    nextDimension: node?.nextDimension || '',
                    nextIsAttribute: node?.nextIsAttribute ?? true,
                    data: data?.data || [],
                    dimIdsFilter: dimIdsFilter,
                };
            } catch (error) {
                // console.error('Data fetch error for node:', node.name, error);
                return {
                    node,
                    nextAttributeType: node?.nextAttributeType || '',
                    nextDimension: node?.nextDimension || '',
                    nextIsAttribute: node?.nextIsAttribute ?? true,
                    data: [],
                    dimIdsFilter: dimIdsFilter,
                };
            }
        },
        [selectedGrowthMarginDriver, singleSelectedTier, timePeriodSelection],
    );

    useEffect(() => {
        const getDimFiltersforCurrentAttributes = async () => {
            if (selectedNodes.length === 0) return;
            setIsDecompositionGraphDataLoading(true);

            const finalNodeData = [];
            let updatedTree = !isEmpty(decompositionGraphData)
                ? cloneDeep(decompositionGraphData)
                : { ...selectedNodes[0] };

            for (const item of selectedNodes) {
                const defaultApplyFilterGenerated = generateDefaultApplyFilter(idForRestOfComponent);
                const tempApplyFilterVariable: any = cloneDeep(defaultApplyFilterGenerated);
                const getPathForCurrentNode = findPath(updatedTree, item.id);
                const applyFilterVarsForCurrentNode = getPathForCurrentNode?.map((path: any) =>
                    getOnlyVarsforApplyFilter(path),
                );

                for (let i = 0; i < applyFilterVarsForCurrentNode.length; i++) {
                    const dimensionKey = `jsonstring_${applyFilterVarsForCurrentNode[i]?.dimension}`;
                    if (tempApplyFilterVariable[dimensionKey]?.inclusion) {
                        const inclusionVal = cloneDeep(tempApplyFilterVariable[dimensionKey].inclusion);
                        tempApplyFilterVariable[dimensionKey].inclusion = [
                            ...inclusionVal,
                            applyFilterVarsForCurrentNode[i],
                        ];
                    } else {
                        tempApplyFilterVariable[dimensionKey] = {
                            inclusion: [applyFilterVarsForCurrentNode[i]],
                        };
                    }
                }

                tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1);
                tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2);
                tempApplyFilterVariable.jsonstring_dim3 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim3);
                tempApplyFilterVariable.jsonstring_dim4 = JSON.stringify(
                    tempApplyFilterVariable?.jsonstring_dim4 || '{}',
                );

                try {
                    const applyFilterResult = await mutateApplyFilter({
                        variables: { ...tempApplyFilterVariable, featureName: 'profit_pool' },
                    });
                    let finalapplyFilterResult;
                    if (applyFilterResult.data) {
                        deleteTypeNameFromResponse(applyFilterResult.data);
                        const applyFilterResponse: DimIdsFilter = {
                            dim1: { ids: applyFilterResult.data.dim1?.ids || [] },
                            dim2: { ids: applyFilterResult.data.dim2?.ids || [] },
                            dim3: { ids: applyFilterResult.data.dim3?.ids || [] },
                            dim4: { ids: applyFilterResult.data.dim4?.ids || [] },
                        };
                        finalapplyFilterResult = getIntersectionOnApplyFilter(
                            idForRestOfComponent,
                            applyFilterResponse,
                        );
                    }
                    const fetchNodeDataResult = await fetchNodeData(
                        item,
                        // selectedNodes[0]
                        (finalapplyFilterResult as DimIdsFilter) || undefined,
                    );
                    finalNodeData.push(fetchNodeDataResult);
                } catch (error) {
                    console.error('applyFilterError', error);
                }
            }
            finalNodeData.forEach((result) => {
                if (result.data && result.data.length > 0) {
                    const childNodes = createChildNodesFromApiData(
                        result.data,
                        selectedGrowthMarginDriver?.key || 'profit_absolute',
                        result.nextAttributeType || '',
                        result.nextDimension || '',
                        undefined,
                        result.node.id,
                        result.node.attributeExId,
                        result.nextIsAttribute ?? true,
                        result.dimIdsFilter,
                    );
                    const sortedChildNodes = orderBy(childNodes, 'kpiValue', 'desc');

                    if (childNodes.length > 0) {
                        updatedTree = addChildrenToNode(
                            updatedTree,
                            result.node.id,
                            result.node.name,
                            sortedChildNodes,
                        );
                    }
                }
            });

            setDecompositionGraphData(updatedTree);
            setIsDecompositionGraphDataLoading(false);
        };

        getDimFiltersforCurrentAttributes();
    }, [selectedNodes]);

    return {
        decompositionGraphData,
        isDecompositionGraphDataLoading,
    };
};

export default useDecompositionGraphData;
